import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is Shippzy?",
      answer: "Shippzy is a Canadian shipping service that helps customers send parcels worldwide at discounted rates. We compare multiple courier companies and provide the best available option based on price, delivery speed, and reliability."
    },
    {
      question: "Which countries does Shippzy ship to?",
      answer: "Shippzy offers worldwide shipping to most countries including India, USA, UK, Australia, Europe, Middle East, and many more destinations."
    },
    {
      question: "Does Shippzy offer door pickup?",
      answer: "Yes. Shippzy provides door-to-door pickup service in many areas. Our team collects your shipment directly from your home or office and sends it through trusted courier partners."
    },
    {
      question: "Do you offer shipping from Canada to India?",
      answer: "Yes. Shipping from Canada to India is one of our most popular services. We provide reliable, fast, and cost-effective shipping options with leading global carriers."
    },
    {
      question: "How does Shippzy offer lower shipping prices?",
      answer: "Shippzy works with multiple courier partners and negotiates discounted rates. Our system compares services from different carriers and selects the best available option for the customer."
    },
    {
      question: "How is shipping weight calculated?",
      answer: "Shipping charges are based on whichever is higher: Actual weight or Dimensional weight. Dimensional weight formula: Length × Width × Height (in cm) ÷ 5000"
    },
    {
      question: "Does Shippzy handle customs clearance?",
      answer: "Yes. Shippzy assists with customs documentation and clearance where applicable. However, any customs duties or taxes charged by the destination country must be paid by the sender or recipient as required by the local authorities."
    },
    {
      question: "Are customs duties included in the shipping price?",
      answer: "No. Customs duties, taxes, or inspection fees imposed by customs authorities are not included in shipping charges and may be payable at the destination country."
    },
    {
      question: "Does my shipment include insurance?",
      answer: "No shipment automatically includes insurance. Optional insurance coverage may be available upon request before shipment is processed. If insurance is not purchased, Shippzy holds no liability for loss or damage beyond carrier policies."
    },
    {
      question: "Can I track my shipment?",
      answer: "Yes. Every shipment comes with a tracking number that allows you to track your parcel in real time."
    },
    {
      question: "How long does delivery take?",
      answer: "Delivery times depend on the destination country and the courier service selected. International deliveries typically take 3–10 business days, depending on the service level."
    },
    {
      question: "What items are not allowed to ship?",
      answer: "Certain items cannot be shipped due to international regulations, including but not limited to: Hazardous materials, Explosives or flammable items, Illegal goods, Restricted pharmaceuticals, Currency or precious metals. Customers are responsible for ensuring their shipment complies with all regulations."
    },
    {
      question: "Can I drop off my parcel at a Shippzy partner location?",
      answer: "Yes. Shippzy partners with local stores such as convenience stores and retail outlets that accept parcel drop-offs."
    },
    {
      question: "Are partner drop-off locations responsible for shipments?",
      answer: "Partner locations act only as drop-off agents. They are responsible for the shipment only until it reaches the Shippzy processing center. Any service promises made by individual partner stores should be verified with Shippzy directly or through our official website."
    },
    {
      question: "What happens if I cancel a pickup?",
      answer: "If a pickup is cancelled, a full refund may be issued. However, if the pickup location is outside the standard service radius (typically beyond 20 km), partial deductions may apply to cover labor and fuel costs."
    },
    {
      question: "What payment methods do you accept?",
      answer: "Shippzy accepts various payment methods including: Credit cards, Debit cards, Online payment methods, Other supported payment options depending on the booking platform."
    },
    {
      question: "Can businesses partner with Shippzy?",
      answer: "Yes. Businesses such as convenience stores, vape shops, and retail stores can partner with Shippzy as parcel drop-off locations. Partners benefit from additional customer traffic and positive cash flow with no joining fees."
    },
    {
      question: "How can I contact Shippzy?",
      answer: "You can contact us through: Phone: +1 437 616 0758, Email: ship@shippzy.ca, Website: www.shippzy.ca"
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Shipping FAQ | International Courier Questions Canada | Shippzy</title>
        <meta name="description" content="Frequently asked questions about international shipping from Canada. Learn about DHL, FedEx, UPS alternatives, pricing, delivery times, customs, and tracking." />
        <link rel="canonical" href="https://www.shippzy.ca/faq" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-300">Find answers to common questions about Shippzy's shipping services</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card
                key={index}
                className="overflow-hidden border-2 hover:border-red-300 transition-colors duration-200"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openIndex === index ? (
                    <ChevronUp className="w-6 h-6 text-red-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-yellow-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-lg mb-6">Our customer support team is available 24/7 to help you</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14376160758"
              className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Call: +1 (437) 616-0758
            </a>
            <a
              href="mailto:ship@shippzy.ca"
              className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Email: ship@shippzy.ca
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default FAQPage;